import message from "components/CustomMessage";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteAnyaConversationMessages,
  getAnyaConversations,
  getConversationIdRequest,
} from "restAPI/AnyaAPI";
import {
  AnyaConversationHistoryItem,
  AnyaConversationMessagesResponse,
  AnyaSessionMode,
} from "types/anya";
import useAnyaAgentSwitcher from "views/Dashboards/AnyaAgentSwitcher/useAnyaAgentSwitcher";

const HISTORY_PANEL_WIDTH = 340;

type AnyaSessionAgent = NonNullable<AnyaConversationMessagesResponse["agent"]>;

type AnyaSessionMetadata = Partial<
  AnyaConversationHistoryItem & AnyaConversationMessagesResponse
>;

type ConversationLookupProps = Parameters<typeof getConversationIdRequest>[0];

export interface UseAnyaModalSessionProps {
  config?: {
    dashboardId?: string;
    existingConversations?: AnyaConversationHistoryItem[];
    title?: string;
    widgetType?: string;
  };
  conversationId?: string | null;
  dashboardId?: string;
  initialSelectedAgent?: AnyaSessionAgent | null;
  initialSessionMode?: AnyaSessionMode;
  invocationContext?: unknown;
  isNewChat?: boolean;
  showSuggestedQuestions?: boolean;
  startWithEmptyPrompt?: boolean;
}

interface ModalSettings {
  close: () => void;
  updateWidth: (updater: (previousWidth: number) => number) => void;
}

interface UseAnyaModalSessionParams {
  props: UseAnyaModalSessionProps;
  isAnyaAgentsEnabled: boolean;
  modalSettings: ModalSettings;
  onCreateNewChat?: () => void;
}

function getDashboardIdFromLocation() {
  if (typeof window === "undefined") {
    return null;
  }

  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  return pathSegments[pathSegments.length - 1] || null;
}

function getSessionAgentId(session?: AnyaSessionMetadata | null) {
  return session?.agent?.id ?? session?.agentId ?? null;
}

function getSessionType(
  historyItem?: AnyaSessionMetadata | null,
  isAnyaAgentsEnabled = true,
) {
  if (!isAnyaAgentsEnabled) {
    return AnyaSessionMode.CHAT;
  }

  if (
    historyItem?.sessionType === AnyaSessionMode.AGENT ||
    getSessionAgentId(historyItem)
  ) {
    return AnyaSessionMode.AGENT;
  }

  return AnyaSessionMode.CHAT;
}

function getAgentFromSession(
  historyItem?: AnyaSessionMetadata | null,
  isAnyaAgentsEnabled = true,
): AnyaSessionAgent | null {
  if (!isAnyaAgentsEnabled) {
    return null;
  }

  const agent = historyItem?.agent;
  const agentId = getSessionAgentId(historyItem);

  if (!agentId) {
    return null;
  }

  return {
    id: agentId,
    name:
      agent?.name || historyItem?.agentName || historyItem?.title || "Agent",
    description: agent?.description || historyItem?.agentDescription || "",
    starterPrompts: agent?.starterPrompts || historyItem?.starterPrompts || [],
  };
}

function getInitialSessionMode(
  props: UseAnyaModalSessionProps,
  isAnyaAgentsEnabled = true,
) {
  return isAnyaAgentsEnabled &&
    props?.initialSessionMode === AnyaSessionMode.AGENT
    ? AnyaSessionMode.AGENT
    : AnyaSessionMode.CHAT;
}

export default function useAnyaModalSession({
  props,
  isAnyaAgentsEnabled,
  modalSettings,
  onCreateNewChat,
}: UseAnyaModalSessionParams) {
  const [chatHistory, setChatHistory] = useState<
    AnyaConversationHistoryItem[] | null
  >(null);
  const [showHistory, setShowHistory] = useState(false);
  const [chatId, setChatIdState] = useState<string | null>(null);
  const [isNewChat, setIsNewChat] = useState(props?.isNewChat || false);
  const [sessionMode, setSessionMode] = useState<AnyaSessionMode>(
    getInitialSessionMode(props, isAnyaAgentsEnabled),
  );
  const [selectedAgent, setSelectedAgent] = useState<AnyaSessionAgent | null>(
    isAnyaAgentsEnabled ? props?.initialSelectedAgent || null : null,
  );
  const { agentOptions, fetchAgents } = useAnyaAgentSwitcher({
    enabled: isAnyaAgentsEnabled,
    initialSelectedAgent: selectedAgent,
    pageSize: 5,
  });
  const [startWithEmptyPrompt, setStartWithEmptyPrompt] = useState(
    props?.startWithEmptyPrompt || false,
  );
  const shouldForceNewChat = props?.isNewChat === true;
  const fetchChatHistoryRef = useRef(() => {});

  useEffect(() => {
    const nextSessionMode = getInitialSessionMode(props, isAnyaAgentsEnabled);
    setSessionMode(nextSessionMode);
    setSelectedAgent(
      nextSessionMode === AnyaSessionMode.AGENT
        ? props?.initialSelectedAgent || null
        : null,
    );
    setChatIdState(props?.conversationId || null);
    setIsNewChat(shouldForceNewChat || !props?.conversationId);
    setStartWithEmptyPrompt(props?.startWithEmptyPrompt || false);
  }, [
    shouldForceNewChat,
    props?.conversationId,
    props?.initialSelectedAgent,
    props?.initialSessionMode,
    props?.startWithEmptyPrompt,
    isAnyaAgentsEnabled,
  ]);

  const applySessionMetadata = useCallback(
    (metadata?: AnyaSessionMetadata | null) => {
      if (!metadata) {
        return;
      }

      const nextSessionType = getSessionType(metadata, isAnyaAgentsEnabled);
      const nextAgent = getAgentFromSession(metadata, isAnyaAgentsEnabled);
      const resolvedSessionType =
        nextSessionType === AnyaSessionMode.AGENT && !nextAgent
          ? AnyaSessionMode.CHAT
          : nextSessionType;

      setSessionMode(resolvedSessionType);
      setSelectedAgent(
        resolvedSessionType === AnyaSessionMode.AGENT ? nextAgent : null,
      );
    },
    [isAnyaAgentsEnabled],
  );

  const initializeAgentSession = useCallback(
    (
      nextAgent: AnyaSessionAgent | null = props?.initialSelectedAgent || null,
    ) => {
      if (!isAnyaAgentsEnabled) {
        setSessionMode(AnyaSessionMode.CHAT);
        setSelectedAgent(null);
        setChatIdState(null);
        setIsNewChat(true);
        return;
      }

      setSessionMode(AnyaSessionMode.AGENT);
      setSelectedAgent(nextAgent);
      setChatIdState(null);
      setIsNewChat(true);
    },
    [isAnyaAgentsEnabled, props?.initialSelectedAgent],
  );

  const setChatId = useCallback((nextChatId?: string | null) => {
    setChatIdState(nextChatId || null);
    setIsNewChat(!nextChatId);
  }, []);

  const resolveInitialChatIdRequest = useCallback(() => {
    if (props?.conversationId) {
      return {
        promise: Promise.resolve(props.conversationId),
        cancel: () => {},
      };
    }

    const lookupProps: ConversationLookupProps = {
      ...props,
      dashboardId:
        props?.dashboardId ||
        props?.config?.dashboardId ||
        getDashboardIdFromLocation() ||
        undefined,
    };

    return getConversationIdRequest(lookupProps);
  }, [props]);

  const initializeChatId = useCallback(async () => {
    if (shouldForceNewChat) {
      setChatIdState(null);
      setIsNewChat(true);
      return;
    }
    if (props?.conversationId) {
      setChatId(props.conversationId);
      return;
    }
    if (props?.initialSessionMode === AnyaSessionMode.AGENT) {
      initializeAgentSession(props?.initialSelectedAgent || null);
      return;
    }
    const request = resolveInitialChatIdRequest();
    const nextChatId = await request.promise;
    setChatId(nextChatId || null);
  }, [
    shouldForceNewChat,
    props?.conversationId,
    props?.initialSessionMode,
    props?.initialSelectedAgent,
    setChatId,
    initializeAgentSession,
    resolveInitialChatIdRequest,
  ]);

  const fetchHistory = useCallback(
    (shouldUpdateChatId = true) => {
      fetchChatHistoryRef.current();
      fetchChatHistoryRef.current = () => {};

      const existingConversations = props?.config?.existingConversations;

      if (existingConversations && existingConversations.length > 0) {
        setChatHistory(existingConversations);
        if (shouldUpdateChatId) {
          void initializeChatId();
        }
        return { cancel: () => {} };
      }

      const request = getAnyaConversations({
        excludeFindingRemediations: true,
      });
      fetchChatHistoryRef.current = request.cancel;

      request.promise
        .then((response) => {
          setChatHistory(response?.data?.history || []);
          if (shouldUpdateChatId) {
            void initializeChatId();
          }
        })
        .catch(() => {
          // Errors are handled by failSafe in makeCancellableAPIRequest (showAPIError).
          // This catch prevents unhandled rejection warnings when the promise is
          // rejected in test environments that bypass failSafe.
        })
        .finally(() => {
          fetchChatHistoryRef.current = () => {};
        });
    },
    [props?.config?.existingConversations, initializeChatId],
  );

  const fetchChatHistory = useCallback(
    (shouldUpdateChatId = true) => {
      fetchHistory(shouldUpdateChatId);
    },
    [fetchHistory],
  );

  useEffect(() => {
    fetchHistory(true);
  }, [fetchHistory]);

  const switchConversation = useCallback(
    (historyItem?: AnyaConversationHistoryItem | null) => {
      if (chatId !== historyItem?.conversationId) {
        applySessionMetadata(historyItem);
        setChatId(historyItem?.conversationId);
        setStartWithEmptyPrompt(false);
      }
    },
    [applySessionMetadata, chatId],
  );

  const createNewChat = useCallback(
    (
      nextMode: AnyaSessionMode = sessionMode,
      nextAgent: AnyaSessionAgent | null = selectedAgent,
    ) => {
      const resolvedMode =
        !isAnyaAgentsEnabled && nextMode === AnyaSessionMode.AGENT
          ? AnyaSessionMode.CHAT
          : nextMode;

      setSessionMode(resolvedMode);
      setSelectedAgent(
        resolvedMode === AnyaSessionMode.AGENT ? nextAgent : null,
      );
      setChatIdState(null);
      setIsNewChat(true);
      setStartWithEmptyPrompt(true);
      onCreateNewChat?.();
    },
    [isAnyaAgentsEnabled, onCreateNewChat, selectedAgent, sessionMode],
  );

  const openChatSession = useCallback(() => {
    createNewChat(AnyaSessionMode.CHAT, null);
  }, [createNewChat]);

  const openAgentSession = useCallback(
    (nextAgent: AnyaSessionAgent | null) => {
      if (!isAnyaAgentsEnabled) {
        return;
      }

      createNewChat(AnyaSessionMode.AGENT, nextAgent);
    },
    [createNewChat, isAnyaAgentsEnabled],
  );

  const deleteConversation = useCallback(
    async (conversationId: string | null = chatId) => {
      if (!conversationId) {
        return;
      }

      try {
        await deleteAnyaConversationMessages(conversationId).promise;
        fetchChatHistory(false);
        message.success("Conversation Deleted");
        createNewChat();
      } catch {
        // already handled by axios
      }
    },
    [chatId, createNewChat, fetchChatHistory],
  );

  const toggleHistory = useCallback(() => {
    setShowHistory((previousValue) => {
      modalSettings.updateWidth((previousWidth) =>
        previousValue
          ? previousWidth - HISTORY_PANEL_WIDTH
          : previousWidth + HISTORY_PANEL_WIDTH,
      );

      return !previousValue;
    });
  }, [modalSettings]);

  return {
    applySessionMetadata,
    agentOptions,
    chatHistory,
    chatId,
    createNewChat,
    deleteConversation,
    fetchChatHistory,
    isNewChat,
    openAgentSession,
    openChatSession,
    searchAgents: fetchAgents,
    selectedAgent,
    sessionMode,
    setChatId,
    showHistory,
    startWithEmptyPrompt,
    switchConversation,
    toggleHistory,
  };
}
